import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  CircularProgress,
  Typography,
  Fab,
  IconButton,
  List,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
} from "@mui/material";
import { Refresh, FilterList, Add, Check, Print } from "@mui/icons-material";
import TableCard from "./TableCard";
import TableModal from "./TableModal";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { useAuth } from "../../../contexts/AuthContext";
import { latinToCyrillic } from "../../../utils/latinToCyrillic";

const API = process.env.REACT_APP_API_BASE;
const TablesTab = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [occupancyFilter, setOccupancyFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const { weddingId } = useAuth();

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API}/get-tables`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      setTables(data);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Грешка при вчитување на маси");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  const handleEdit = (tableData) => {
    setSelectedTable(tableData);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedTable(null);
    setModalOpen(true);
  };

  const handleDeleteTable = (tableId) => {
    setTables((prevTables) =>
      prevTables.filter((table) => table.id !== tableId)
    );
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleOpenFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleSelectOccupancy = (value) => {
    setOccupancyFilter(value);
    setFilterAnchorEl(null);
  };

  const handleSelectType = (value) => {
    setTypeFilter(value);
    setFilterAnchorEl(null);
  };

  const filteredTables = useMemo(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    const queryVariants = trimmedQuery
      ? [trimmedQuery, latinToCyrillic(trimmedQuery)]
      : [];

    return tables.filter((table) => {
      const capacity =
        typeof table.available_seats === "number"
          ? table.available_seats
          : 0;
      const taken =
        typeof table.taken_seats === "number" ? table.taken_seats : 0;
      const remaining = capacity - taken;
      const isFull = remaining <= 0;

      if (occupancyFilter === "full" && !isFull) {
        return false;
      }
      if (occupancyFilter === "available" && isFull) {
        return false;
      }

      if (typeFilter !== "all" && table.type !== typeFilter) {
        return false;
      }

      if (!queryVariants.length) {
        return true;
      }

      const haystack = `${table.name ?? ""}`.toLowerCase();
      const cyrillicHaystack = latinToCyrillic(haystack);

      return queryVariants.some(
        (variant) =>
          variant && (haystack.includes(variant) || cyrillicHaystack.includes(variant))
      );
    });
  }, [tables, searchQuery, occupancyFilter, typeFilter]);

  const availableTypes = useMemo(() => {
    return Array.from(
      new Set(
        tables
          .map((table) => table.type)
          .filter((type) => typeof type === "string" && type.length > 0)
      )
    );
  }, [tables]);

  const isFilterMenuOpen = Boolean(filterAnchorEl);
  const hasActiveFilters =
    occupancyFilter !== "all" || typeFilter !== "all";

  return (
    <Grid
      item
      className="organizer-tab-container"
      size={{ xs: 10, md: 8, lg: 6, xl: 4 }}
    >
      <Box className="search-bar">
        <TextField
          fullWidth
          placeholder="Пребарувај"
          variant="outlined"
          size="small"
          className="search-input"
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <IconButton color="inherit" onClick={fetchTables}>
          <Refresh />
        </IconButton>
        <IconButton color="inherit" onClick={() => window.open('/tables/print', '_blank') } title="Печати">
          <Print />
        </IconButton>
        <IconButton
          color={hasActiveFilters ? "primary" : "inherit"}
          onClick={handleOpenFilter}
        >
          <FilterList />
        </IconButton>
      </Box>
      {filteredTables.length >= 3 ? (
        <Button className="quick-add-strip" endIcon={<Add />} onClick={handleAdd}>
          Додадете маси
        </Button>
      ) : null}
      <Menu
        anchorEl={filterAnchorEl}
        open={isFilterMenuOpen}
        onClose={handleCloseFilter}
      >
        <Typography
          component="div"
          sx={{ px: 2, py: 1, fontWeight: 600, fontSize: 13, color: "text.secondary" }}
        >
          Пополнетост
        </Typography>
        {[
          { value: "all", label: "Сите маси" },
          { value: "available", label: "Слободни" },
          { value: "full", label: "Пополнети" },
        ].map((option) => (
          <MenuItem
            key={option.value}
            selected={occupancyFilter === option.value}
            onClick={() => handleSelectOccupancy(option.value)}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {occupancyFilter === option.value ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
        {availableTypes.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography
              component="div"
              sx={{ px: 2, py: 1, fontWeight: 600, fontSize: 13, color: "text.secondary" }}
            >
              Тип на маса
            </Typography>
            <MenuItem
              selected={typeFilter === "all"}
              onClick={() => handleSelectType("all")}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {typeFilter === "all" ? <Check fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>Сите типови</ListItemText>
            </MenuItem>
            {availableTypes.map((type) => (
              <MenuItem
                key={type}
                selected={typeFilter === type}
                onClick={() => handleSelectType(type)}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {typeFilter === type ? <Check fontSize="small" /> : null}
                </ListItemIcon>
                <ListItemText>{type}</ListItemText>
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      {loading ? (
        <Box
          className="organizer-list"
          display="flex"
          justifyContent="center"
          p={2}
        >
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Typography color="error" textAlign="center" mt={2}>
          {error}
        </Typography>
      ) : filteredTables.length > 0 ? (
        <>
          <Box className="organizer-list">
            <List>
              {filteredTables.map((table) => (
                <TableCard
                  key={table.id}
                  number={table.number}
                  name={table.name}
                  type={table.type}
                  takenSeats={table.taken_seats}
                  availableSeats={table.available_seats}
                  id={table.id}
                  onEdit={handleEdit}
                  onDelete={handleDeleteTable}
                />
              ))}
            </List>
          </Box>

          <Box
            className="fab-container"
            display="flex"
            justifyContent="flex-end"
          >
            <Fab className="add-fab" onClick={handleAdd}>
              <Add />
            </Fab>
          </Box>
        </>
      ) : (
        <Grid
          container
          spacing={2}
          direction={{ xs: "column", md: "row" }}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item>
            <Typography variant="h6" textAlign={{ xs: "center", md: "left" }}>
              {searchQuery || hasActiveFilters
                ? "Нема маси што ги исполнуваат условите."
                : "На копчето додадете маси"}
            </Typography>
          </Grid>
          <Grid item>
            <Fab className="add-fab" onClick={handleAdd}>
              <Add />
            </Fab>
          </Grid>
        </Grid>
      )}

      <TableModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialData={selectedTable}
        onSuccess={() => {
          fetchTables();
          setModalOpen(false);
        }}
        weddingId={weddingId}
      />
    </Grid>
  );
};

export default TablesTab;
